import { FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useBeforeUnload, useBlocker } from "react-router";

import { ConfirmDialog } from "primereact/confirmdialog";

import { translationKeys } from "../../../../models/app-translation-keys";

interface ICanDeactivatePendingChangesDialogProps {
  hasPendingChanges: boolean;
  rejectButtonText?: string;
  acceptButtonText?: string;
  message?: string;
}

export const CanDeactivatePendingChangesDialog: FunctionComponent<ICanDeactivatePendingChangesDialogProps> = (
  props: ICanDeactivatePendingChangesDialogProps,
) => {
  const { t } = useTranslation();
  const printMessage = props.message ?? t(translationKeys.confirmation.confirmLeavePendingChanges);
  const rejectLabel = props.rejectButtonText ?? t(translationKeys.actions.cancel);
  const acceptLabel = props.acceptButtonText ?? t(translationKeys.actions.confirm);

  const blocker = useBlocker(useCallback(() => props.hasPendingChanges, [props.hasPendingChanges])); // prevents navigation when there are pending changes
  const prevBlockerState = useRef(blocker.state);
  const [isCanDeactivateDialogVisible, setIsCanDeactivateDialogVisible] = useState(false);

  const accept = () => {
    if (blocker.proceed) {
      blocker.proceed();
    }
    setIsCanDeactivateDialogVisible(false);
  };

  const reject = () => {
    if (blocker.reset) {
      blocker.reset();
    }
    setIsCanDeactivateDialogVisible(false);
  };

  // resets blocker to allow navigation when user clicked the X button
  const handleDialogHide = () => {
    if (blocker.reset) {
      blocker.reset();
    }
    setIsCanDeactivateDialogVisible(false);
  };

  // handles dialog visibility based on blocker state transitions
  useEffect(() => {
    // detects edge transition: blocker became blocked (user tried to navigate)
    const becameBlocked = blocker.state === "blocked" && prevBlockerState.current !== "blocked";

    // opens dialog only when when blocker transitions to blicked and there are pending changes (navigation is now allowed)
    if (becameBlocked && props.hasPendingChanges) {
      setIsCanDeactivateDialogVisible(true);
    }

    // closes dialog when blocker transitions to unblocked (navigation is allowed)
    if (blocker.state === "unblocked") {
      setIsCanDeactivateDialogVisible(false);
    }

    // resets blocker to allow navigation if pending changes cleared while blocked
    if (!props.hasPendingChanges && blocker.state === "blocked") {
      handleDialogHide();
    }

    // updates previous state for next transition detection
    prevBlockerState.current = blocker.state;
  }, [blocker.state, props.hasPendingChanges, prevBlockerState.current]);

  useBeforeUnload(
    useCallback(
      event => {
        if (props.hasPendingChanges) {
          event.preventDefault();
          event.returnValue = printMessage;
        }
      },
      [props.hasPendingChanges],
    ),
    { capture: true },
  );

  return (
    <ConfirmDialog
      message={props.message}
      header={t(translationKeys.actions.confirmAction)}
      icon="pi pi-exclamation-triangle"
      defaultFocus="reject"
      acceptClassName="p-button-danger"
      acceptLabel={acceptLabel}
      rejectLabel={rejectLabel}
      accept={accept}
      reject={reject}
      onHide={handleDialogHide}
      visible={isCanDeactivateDialogVisible}
    />
  );
};
